import { TimeRangeConfig } from '@/api/generated/model';
import { getLiveActivitySectionFromPath, isLiveActivityPath } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import { isNebulyPagePath } from '@/domains/pages/constants';
import { getNebulyPageFilterBarDefault } from '@/domains/pages/utils/nebuly-page-time-range';

const REPORTS_PAGE_PATH = '/reports';
const EXPLORE_PAGE_PATH = '/explore';

export function parseReportSharedTimeRangeFromParam(param: string | null): TimeRangeConfig | undefined {
  if (!param) return undefined;

  try {
    const parsed = JSON.parse(decodeURIComponent(param)) as TimeRangeConfig;
    if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function resolveGlobalFilterPickerTimeRange({
  pathname,
  liveActivityCohortsTimeRange,
  reportSharedTimeRangeParam,
  pageTimeRangeParam,
  exploreTimeRange,
  contextTimeRange,
  now,
}: {
  pathname: string;
  liveActivityCohortsTimeRange: TimeRangeConfig;
  reportSharedTimeRangeParam: string | null;
  pageTimeRangeParam: string | null;
  exploreTimeRange: TimeRangeConfig | undefined;
  contextTimeRange: TimeRangeConfig;
  now?: Date;
}): TimeRangeConfig | undefined {
  // On the Reports page the picker must scope to that page's own calendar, which is
  // mirrored into the reportSharedTimeRange URL param whenever the report time range changes.
  if (pathname === REPORTS_PAGE_PATH) {
    return parseReportSharedTimeRangeFromParam(reportSharedTimeRangeParam);
  }

  // Nebuly system pages with a filter bar scope the left-rail picker to pageTimeRange.
  if (isNebulyPagePath(pathname)) {
    return parseReportSharedTimeRangeFromParam(pageTimeRangeParam) ?? getNebulyPageFilterBarDefault(now);
  }

  // On Explore the picker must scope to Explore's own calendar (exploreConfig.time_range),
  // which lives in session storage keyed by the explore key.
  if (pathname === EXPLORE_PAGE_PATH) {
    return exploreTimeRange;
  }

  // On Live Activity cohorts tab, scope filter values to a rolling one-year window.
  if (isLiveActivityPath(pathname) && getLiveActivitySectionFromPath(pathname) === 'cohorts') {
    return liveActivityCohortsTimeRange;
  }

  // Everywhere else (Live Data, home, …) the page's calendar IS the global filters context.
  return contextTimeRange;
}
